/**
 * §7.1 Pathogen Watch — default home.
 *
 * Fully data-driven from the live surveillance feed (CDC NWSS · WHO DON ·
 * CoV-Spectrum LAPIS, ingested via `npm run ingest`). NO fabricated data:
 * every KPI, the outbreak-events panel, and the feed table derive from real
 * ingested signals. When no snapshot is present the surface shows an honest
 * "no live data" state rather than invented numbers.
 */

import { useMemo, useState, useRef } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { listRegions } from '@biomax/plugin-region-database';
import { listSignals, signalFreshness, sourceHealth } from '@biomax/plugin-surveillance-feed';
import { FEED_GENERATED_AT, FEED_IS_LIVE, FEED_SOURCES } from '../catalog';
import { OutbreakMap, colorFor, familyFor, FAMILY_LABEL, type OutbreakMapHandle, type PathogenFamily } from '../OutbreakMap';
import { signalThreatTier, THREAT_TIER_META } from '../threatTier';
import { ProvChip, provKind } from '../ProvChip';
import { triStateOf, setManyHidden, toggleTarget } from '../tracked-pathogens';

/** Metrics that aren't situational incidence (kept off the worst-first triage). */
const NON_SITUATIONAL_METRICS = new Set([
  'sequences', 'seq-frequency', 'test-positivity', 'syndromic-rate', 'hospitalisation-rate',
]);

// Catalog-driven "dangerous" test: high infection-fatality, or a pathogen the
// catalog doesn't recognise (novel → warrants attention). No hardcoded lists.
function isDangerous(pathogenId: string): boolean {
  const p = findPathogen(pathogenId);
  if (!p) return true; // unverified-novel / not in catalog
  const mid = p.ifrByAgeBand[Math.floor(p.ifrByAgeBand.length / 2)]?.ifr ?? 0;
  return mid > 0.05;
}

function fmtAsOf(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

/** Human-readable cadence (e.g. 30m / 6h / 3d) for the feed-health panel. */
function fmtCadence(ms: number): string {
  const h = ms / 3_600_000;
  if (h < 1) return `${Math.round(ms / 60_000)}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

/** Triage-row trend (CD R3b extension). Compact inline SVG sparkline reading
 *  `signal.history`. Width-fixed so the triage grid stays uniform; renders an
 *  em-dash placeholder when the signal has no history (e.g. WHO DON events,
 *  which are point-in-time notifications, not time series). */
function TriageTrend({ history, tierColor }: { history?: Array<{ asOf: string; value: number }>; tierColor: string }) {
  if (!history || history.length < 2) {
    // CD R3b note: explicit "no history" rather than bare em-dash so the
    // operator doesn't misread absence-of-series as steady-state.
    return (
      <span
        title="This signal is a point-in-time notification — no trend series."
        style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 60, gap: 3, font: '600 10px var(--bm-font-mono)', color: 'var(--bm-text-subtle)', opacity: 0.7 }}
      >
        <span aria-hidden>∅</span>
        <span style={{ fontSize: 9, letterSpacing: '0.03em' }}>no hist</span>
      </span>
    );
  }
  const values = history.map(p => p.value);
  const w = 60, h = 22;
  const min = Math.min(...values), max = Math.max(...values), range = max - min || 1;
  const X = (i: number): number => (i / (values.length - 1)) * w;
  const Y = (v: number): number => h - ((v - min) / range) * (h - 4) - 2;
  const pts = values.map((v, i) => `${X(i)},${Y(v)}`).join(' ');
  const last = values[values.length - 1]!;
  const prev = values[values.length - 2]!;
  const up = last > prev;
  const flat = last === prev;
  return (
    <span
      style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
      title={`${values.length}-point trend: ${values.map(v => v.toLocaleString()).join(' → ')}`}
    >
      <svg width={w} height={h} aria-hidden style={{ display: 'block' }}>
        <polyline points={pts} fill="none" stroke={tierColor} strokeWidth="1.5" strokeLinejoin="round" opacity={0.85} />
        <circle cx={X(values.length - 1)} cy={Y(last)} r="2.2" fill={tierColor} />
      </svg>
      <span style={{ font: '600 11px var(--bm-font-mono)', color: tierColor, opacity: 0.85 }}>{flat ? '–' : up ? '▲' : '▼'}</span>
    </span>
  );
}

/** Tri-state checkbox glyph (CD tidy-up §C) for the Tracked-Pathogens
 *  Select-all + per-category controls. 'all' = ✓, 'some' = – (partial),
 *  'none' = empty. Shape + glyph carry the state (never colour-only). */
function TriCheck({ state }: { state: 'all' | 'some' | 'none' }) {
  return (
    <span className={`tp-tri tp-tri--${state}`} aria-hidden>
      {state === 'all' ? '✓' : state === 'some' ? '–' : ''}
    </span>
  );
}

export function PathogenWatch() {
  const allPathogens = listPathogens();
  const allRegions = listRegions();

  const feed = useMemo(() => {
    const now = Date.now();
    return listSignals()
      .map(s => ({ ...s, fresh: signalFreshness(s, now) }))
      .sort((a, b) => Date.parse(b.asOf) - Date.parse(a.asOf));
  }, []);

  // Threat triage — the worst-first headline (CD review H1). Situational signals
  // ranked by catalog-derived threat tier, then recency. Stale demoted, unverified
  // capped below CRITICAL, genomic→WATCH (handled inside signalThreatTier).
  const triage = useMemo(() => {
    const now = Date.now();
    return listSignals()
      .filter(s => !NON_SITUATIONAL_METRICS.has(s.metric))
      .map(s => ({ s, tr: signalThreatTier(s, { stale: signalFreshness(s, now).stale }) }))
      .sort((a, b) =>
        THREAT_TIER_META[b.tr.tier].rank - THREAT_TIER_META[a.tr.tier].rank ||
        Date.parse(b.s.asOf) - Date.parse(a.s.asOf))
      .slice(0, 6);
  }, []);

  // Per-source feed health (SLO surfacing) — ingest status × signal freshness.
  // Derived from the live signals + the real ingest SourceReports only.
  const health = useMemo(
    () => sourceHealth(listSignals(), FEED_SOURCES, Date.now())
      .sort((a, b) => Number(a.ok) - Number(b.ok) || a.id.localeCompare(b.id)),
    [],
  );

  // Everything below is derived from the real feed.
  const confirmed = feed.filter(s => s.verificationStatus === 'confirmed');
  const conflicted = feed.filter(s => (s.context?.conflictingReports?.length ?? 0) > 0);
  const donEvents = feed.filter(s => s.sources.includes('who-don'))
    .sort((a, b) => Date.parse(b.asOf) - Date.parse(a.asOf));
  const variantSignals = feed.filter(s => s.metric === 'seq-frequency');
  const dangerous = feed.filter(s => isDangerous(s.pathogenId));
  const distinctPathogens = new Set(feed.map(s => s.pathogenId)).size;
  const liveSources = FEED_SOURCES.filter(s => s.ok).length;

  // Map interactivity: click-to-zoom handle + per-pathogen visibility filter.
  const mapApi = useRef<OutbreakMapHandle>(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const toggleHidden = (pid: string) =>
    setHidden(prev => { const n = new Set(prev); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; });

  // M3: legend filter (search) + "only active" toggle (only pathogens with at
  // least one non-stale situational signal currently).
  const [legendFilter, setLegendFilter] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const activePathogens = useMemo(() => {
    const now = Date.now();
    const set = new Set<string>();
    for (const s of listSignals()) {
      if (NON_SITUATIONAL_METRICS.has(s.metric)) continue;
      if (!signalFreshness(s, now).stale) set.add(s.pathogenId);
    }
    return set;
  }, []);

  // Events sorted by severity (dangerous → moderate), then recency.
  const sortedEvents = [...donEvents].sort((a, b) =>
    (Number(isDangerous(b.pathogenId)) - Number(isDangerous(a.pathogenId))) ||
    (Date.parse(b.asOf) - Date.parse(a.asOf)));
  // Distinct mappable pathogens (drives the Tracked Pathogens legend); dangerous first.
  const trackedPathogens = [...new Set(feed.filter(s => s.metric !== 'seq-frequency').map(s => s.pathogenId))]
    .sort((a, b) => (Number(isDangerous(b)) - Number(isDangerous(a))) || a.localeCompare(b));

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.1</span>
        <h1 className="surface__title">Pathogen Watch · global signal index</h1>
      </div>
      <div className="surface__sub">
        {FEED_IS_LIVE
          ? <>Live feed · as of {fmtAsOf(FEED_GENERATED_AT!)} · {liveSources}/{FEED_SOURCES.length} sources · {allPathogens.length} pathogens + {allRegions.length} regions in catalog</>
          : <>No live data — run <code>npm run ingest</code> · catalog: {allPathogens.length} pathogens, {allRegions.length} regions</>}
        {' '}· EDU — NOT FOR OPERATIONAL DEPLOYMENT
      </div>

      {!FEED_IS_LIVE && (
        <div className="panel panel--accent" style={{ marginBottom: 'var(--bm-space-4)' }}>
          <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-sm)', color: 'var(--bm-text-muted)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--bm-text)' }}>No surveillance snapshot present.</strong> This build ships no fabricated
            data. Run <code>NODE_OPTIONS=--use-system-ca npm run ingest</code> to pull live signals from the open sources
            (CDC NWSS · WHO DON · CoV-Spectrum LAPIS), then reload. The map + tables below populate from that real snapshot only.
          </div>
        </div>
      )}

      {/* THREAT TRIAGE — worst-first headline, open by default (CD review H1). */}
      {triage.length > 0 && (
        <div className="panel" style={{ borderLeft: '3px solid var(--bm-threat-1)', marginBottom: 'var(--bm-space-4)' }}>
          <div className="panel__head">
            <span className="panel__title">Threat triage · worst first</span>
            <span className="panel__meta">top {triage.length} active · ranked by tier, then recency</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8, padding: '4px 0' }}>
            {triage.map(({ s, tr }, i) => {
              const tm = THREAT_TIER_META[tr.tier];
              return (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  title="Zoom the map to this outbreak"
                  onClick={() => mapApi.current?.focus(s)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); mapApi.current?.focus(s); } }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderLeft: `3px solid ${tm.color}`, background: 'var(--bm-bg-subtle)', borderRadius: 6, cursor: 'pointer' }}
                >
                  <span style={{ font: '700 10px var(--bm-font-mono)', color: tm.color, border: `1px solid ${tm.color}`, borderRadius: 999, padding: '1px 7px', flex: 'none' }}>{tm.label}</span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600, fontSize: 14 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: colorFor(s.pathogenId), flex: 'none' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {findPathogen(s.pathogenId)?.names.common ?? s.pathogenId}{s.subtype ? ` · ${s.subtype}` : ''}
                      </span>
                    </span>
                    <span style={{ display: 'block', fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.location.admin} · {s.metric === 'event-notification' ? 'event' : `${s.metric} ${s.value.toLocaleString()}`} · {fmtAsOf(s.asOf)}
                    </span>
                  </span>
                  <TriageTrend history={s.history} tierColor={tm.color} />
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--bm-text-subtle)', padding: '6px 12px 0', lineHeight: 1.5 }}>
            Tier = catalog severity (IFR) × transmissibility (R0); stale demoted, unverified capped below CRITICAL. Click a row to zoom the map.
          </div>
        </div>
      )}

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Confirmed signals</div>
          <div className="kpi__value kpi__value--accent">{confirmed.length}</div>
          <div className="kpi__sub">of {feed.length} ingested</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Dangerous detections</div>
          <div className="kpi__value kpi__value--danger">{dangerous.length}</div>
          <div className="kpi__sub">high-IFR or novel pathogens</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">WHO DON events</div>
          <div className="kpi__value kpi__value--signal">{donEvents.length}</div>
          <div className="kpi__sub">official outbreak news</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Variant signals</div>
          <div className="kpi__value">{variantSignals.length}</div>
          <div className="kpi__sub">lineage frequencies (LAPIS)</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Pathogens in feed</div>
          <div className="kpi__value">{distinctPathogens}</div>
          <div className="kpi__sub">{liveSources}/{FEED_SOURCES.length} sources live</div>
        </div>
      </div>

      <div className="watch-grid">
        <div>
          <OutbreakMap
            ref={mapApi}
            hiddenPathogens={hidden}
            onJumpToCountryProfile={iso3 => { window.location.hash = `#/country-profile?iso3=${iso3}`; }}
          />
          <div className="watch-map-caption">
            Markers plotted from ingested signals · filled = confirmed, hollow-dashed = unverified ·
            {FEED_IS_LIVE ? ` LIVE · as of ${fmtAsOf(FEED_GENERATED_AT!)}` : ' no live data — run npm run ingest'}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-4)' }}>
          {/* Active outbreak events — collapsible, severity-sorted, click-to-zoom. */}
          <div className="panel panel--accent">
            <button
              type="button"
              className="panel__head"
              onClick={() => setEventsOpen(o => !o)}
              aria-expanded={eventsOpen}
              style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }}
            >
              <span className="panel__title">
                <span style={{ display: 'inline-block', width: 14, color: 'var(--bm-text-muted)' }}>{eventsOpen ? '▾' : '▸'}</span>
                Active outbreak events
                {donEvents.length > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 9, background: 'var(--bm-bg-subtle)', color: 'var(--bm-text-muted)' }}>{donEvents.length}</span>
                )}
              </span>
              <span className="panel__meta">WHO DON · official</span>
            </button>
            {eventsOpen && (donEvents.length === 0 ? (
              <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-xs)', color: 'var(--bm-text-muted)' }}>
                No WHO DON events in the current snapshot.
              </div>
            ) : (
              <>
                {sortedEvents.map((s, i) => (
                  <div
                    key={i}
                    className="watch-pathogen"
                    role="button"
                    tabIndex={0}
                    title="Zoom the map to this outbreak"
                    onClick={() => mapApi.current?.focus(s)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); mapApi.current?.focus(s); } }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={`watch-pathogen__glyph watch-pathogen__glyph--${isDangerous(s.pathogenId) ? 'major' : 'moderate'}`}>
                      {isDangerous(s.pathogenId) ? '!' : '▲'}
                    </span>
                    <span className="watch-pathogen__name">{s.pathogenId}</span>
                    <span className="watch-pathogen__meta">
                      {s.location.admin} ·{' '}
                      {s.metric === 'cases' ? `${s.value.toLocaleString()} confirmed` : 'event'}
                      {' · '}{fmtAsOf(s.asOf)}
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: 12, fontSize: 11, color: 'var(--bm-text-muted)' }}>
                  Sorted by severity (high-IFR / novel first). Click an event to zoom the map to it.
                  Counts are confirmed-case figures parsed from the WHO DON report; "event" = no confirmed count.
                </div>
              </>
            ))}
          </div>

          {/* Tracked pathogens — nested by CATEGORY (transmission-route /
              taxonomy family, derived from familyFor() — a real catalog-driven
              field, NOT an invented grouping) with a global Select-all /
              Unselect-all + per-category tri-state checkboxes (CD tidy-up §C).
              Plus the existing filter + only-active (M3). Selection is the
              inverse of the `hidden` Set the map reads. */}
          {(() => {
            const filtered = trackedPathogens.filter(pid => {
              if (onlyActive && !activePathogens.has(pid)) return false;
              if (!legendFilter) return true;
              const q = legendFilter.toLowerCase();
              const name = (findPathogen(pid)?.names.common ?? pid).toLowerCase();
              return name.includes(q) || pid.toLowerCase().includes(q);
            });
            const byFamily = new Map<PathogenFamily, string[]>();
            for (const pid of filtered) {
              const f = familyFor(pid);
              (byFamily.get(f) ?? byFamily.set(f, []).get(f)!).push(pid);
            }
            const familyOrder: PathogenFamily[] = ['haemorrhagic', 'respiratory', 'vector', 'enteric', 'zoonotic'];
            // Tri-state + set-many are pure helpers (tracked-pathogens.ts) so
            // the partial-selection behaviour is unit-tested. shown = selected;
            // `hidden` is the inverse set the map reads.
            const triState = (pids: readonly string[]) => triStateOf(pids, hidden);
            const setMany = (pids: readonly string[], selected: boolean) =>
              setHidden(prev => setManyHidden(prev, pids, selected));
            const toggleFamily = (f: PathogenFamily) => {
              const pids = byFamily.get(f) ?? [];
              setMany(pids, toggleTarget(triState(pids))); // any not-all → select all; all → clear
            };
            const globalState = triState(filtered);
            const toggleAll = () => setMany(filtered, toggleTarget(globalState));
            const shownCount = filtered.filter(p => !hidden.has(p)).length;
            return (
              <div className="panel">
                <div className="panel__head">
                  <span className="panel__title">Tracked pathogens</span>
                  {/* Global Select-all / Unselect-all with a tri-state box. */}
                  <button
                    type="button"
                    className="tp-allbtn"
                    onClick={toggleAll}
                    disabled={filtered.length === 0}
                    title={globalState === 'all' ? 'Unselect all pathogens' : 'Select all pathogens'}
                    aria-label={`${globalState === 'all' ? 'Unselect' : 'Select'} all pathogens — ${shownCount} of ${filtered.length} currently shown`}
                  >
                    <TriCheck state={globalState} />
                    {globalState === 'all' ? 'Unselect all' : 'Select all'}
                    <span style={{ color: 'var(--bm-text-subtle)' }}>{shownCount}/{filtered.length}</span>
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, padding: '6px 12px 4px', alignItems: 'center' }}>
                  <input
                    type="search"
                    placeholder="filter pathogens…"
                    value={legendFilter}
                    onChange={e => setLegendFilter(e.target.value)}
                    style={{ flex: 1, font: '12px var(--bm-font-mono, ui-monospace, monospace)', padding: '4px 8px', background: 'var(--bm-bg-subtle)', color: 'var(--bm-text)', border: '1px solid var(--bm-border)', borderRadius: 5 }}
                  />
                  <label title="Hide pathogens that have no current (non-stale) signals" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--bm-text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} />
                    only active
                  </label>
                </div>
                {filtered.length === 0 ? (
                  <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-xs)', color: 'var(--bm-text-muted)' }}>
                    {trackedPathogens.length === 0 ? 'No mappable signals in the current snapshot.' : 'No pathogens match the filter.'}
                  </div>
                ) : (
                  <div style={{ padding: '4px 0' }}>
                    {familyOrder.filter(f => byFamily.has(f)).map(f => {
                      const pids = byFamily.get(f)!;
                      const st = triState(pids);
                      return (
                        <div key={f} style={{ marginTop: 4 }}>
                          <button
                            type="button"
                            onClick={() => toggleFamily(f)}
                            title={st === 'all' ? `Unselect all ${FAMILY_LABEL[f]}` : `Select all ${FAMILY_LABEL[f]}`}
                            aria-label={`${FAMILY_LABEL[f]} category — ${st === 'all' ? 'all selected' : st === 'none' ? 'none selected' : 'some selected'}; ${st === 'all' ? 'unselect' : 'select'} all`}
                            className="tp-cathead"
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--bm-text-subtle)', font: '600 10px/1 var(--bm-font-mono, ui-monospace, monospace)', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'left' }}
                          >
                            <TriCheck state={st} />
                            <span style={{ flex: 1 }}>{FAMILY_LABEL[f]}</span>
                            <span>{pids.filter(p => !hidden.has(p)).length}/{pids.length}</span>
                          </button>
                          {pids.map(pid => {
                            const visible = !hidden.has(pid);
                            return (
                              <label key={pid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px 4px 30px', cursor: 'pointer', fontSize: 13, opacity: visible ? 1 : 0.5 }}>
                                <input type="checkbox" checked={visible} onChange={() => toggleHidden(pid)} />
                                <span style={{ width: 9, height: 9, borderRadius: '50%', background: colorFor(pid), flex: 'none' }} />
                                <span style={{ flex: 1 }}>{findPathogen(pid)?.names.common ?? pid}</span>
                                {activePathogens.has(pid) && <span title="current (non-stale) signal" style={{ color: 'var(--bm-prov-confirmed)', fontSize: 10 }}>●</span>}
                                {isDangerous(pid) && <span title="high-IFR or novel" style={{ color: 'var(--bm-signal)' }}>⚠</span>}
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--bm-text-muted)', padding: '0 12px', lineHeight: 1.5 }}>
                      Nested by category (transmission-route / taxonomy family). The category box shows
                      <b style={{ color: 'var(--bm-text)' }}> ✓ </b>(all),<b style={{ color: 'var(--bm-text)' }}> – </b>(some), or empty (none) selected;
                      the Select-all mirrors it. Untick a pathogen — or a category — to hide its markers.
                      ● = current signal, ⚠ = high-IFR or novel.
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 'var(--bm-space-4)' }}>
        <div className="panel__head">
          <span className="panel__title">Feed health</span>
          <span className="panel__meta">
            {FEED_IS_LIVE
              ? `${liveSources}/${FEED_SOURCES.length} sources ok · ${health.filter(h => h.stale).length} stale`
              : 'no live data'}
          </span>
        </div>
        {health.length === 0 ? (
          <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-sm)', color: 'var(--bm-text-muted)' }}>
            No live data — run <code>npm run ingest</code> to populate per-source health from the open sources.
          </div>
        ) : (
          <table className="dtable">
            <thead>
              <tr>
                <th>Source</th><th>Status</th><th className="num">Signals</th>
                <th>Cadence</th><th>Latest as-of</th><th>Freshness</th>
              </tr>
            </thead>
            <tbody>
              {health.map(h => (
                <tr key={h.id}>
                  <td>{h.id}</td>
                  <td>
                    <ProvChip kind={h.ok ? 'confirmed' : 'conflict'} title={h.ok ? 'ingest ok' : `degraded — ${h.error ?? 'no error reason'}`} />
                    {h.error ? <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--bm-text-muted)' }}>· {h.error}</span> : null}
                  </td>
                  <td className="num">{h.signalCount}</td>
                  <td style={{ color: 'var(--bm-text-muted)' }}>{fmtCadence(h.cadenceMs)}</td>
                  <td style={{ color: 'var(--bm-text-muted)' }}>{h.latestAsOf ? fmtAsOf(h.latestAsOf) : '—'}</td>
                  <td>
                    {h.latestAsOf
                      ? <ProvChip kind={h.stale ? 'stale' : 'confirmed'} title={h.stale ? 'freshest signal older than 2× source cadence' : 'within source cadence'} />
                      : <span style={{ color: 'var(--bm-text-muted)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 11, color: 'var(--bm-text-subtle)', padding: '8px 12px', lineHeight: 1.5 }}>
          Per-source SLO: ingest status fused with live-signal freshness. STALE = freshest signal older than
          2× the source's native cadence. Derived from real ingest reports only — no fabricated health.
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Live surveillance feed</span>
          <span className="panel__meta">
            {confirmed.length} confirmed · {feed.length} total
            {conflicted.length > 0 ? ` · ${conflicted.length} source-conflict${conflicted.length > 1 ? 's' : ''}` : ''} ·
            {FEED_IS_LIVE
              ? ` LIVE · ${liveSources}/${FEED_SOURCES.length} sources · as of ${fmtAsOf(FEED_GENERATED_AT!)}`
              : ' no live data'}
          </span>
        </div>
        {feed.length === 0 ? (
          <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-sm)', color: 'var(--bm-text-muted)' }}>
            No signals. Run <code>npm run ingest</code> to populate from the live open sources.
          </div>
        ) : (
          <table className="dtable">
            <thead>
              <tr>
                <th>Pathogen</th><th>Location</th><th>Metric</th><th className="num">Value</th>
                <th>Source</th><th>Status</th><th>As-of</th>
              </tr>
            </thead>
            <tbody>
              {feed.map((s, i) => {
                const conflicts = s.context?.conflictingReports;
                return (
                <tr key={i}>
                  <td>{s.pathogenId}{s.subtype ? ` · ${s.subtype}` : ''}</td>
                  <td>{s.location.admin}</td>
                  <td>{s.metric}</td>
                  <td className="num">
                    {s.metric === 'event-notification' ? '— (event)' : s.value.toLocaleString()}
                    {conflicts && conflicts.length > 0 && (
                      <span
                        title={`Sources disagree — primary ${s.value.toLocaleString()} (${s.sources[0]}) vs ${conflicts.map(c => `${c.value.toLocaleString()} (${c.source})`).join(', ')}`}
                        style={{ marginLeft: 6, color: 'var(--bm-danger)', fontFamily: 'var(--bm-font-mono)', fontSize: 11, cursor: 'help' }}
                      >
                        ⚠ {s.value.toLocaleString()} vs {conflicts.map(c => c.value.toLocaleString()).join('/')}
                      </span>
                    )}
                  </td>
                  <td>{s.sources.join(', ')}</td>
                  <td><ProvChip kind={provKind(s, s.fresh.stale)} /></td>
                  <td style={{ color: s.fresh.stale ? 'var(--bm-danger)' : 'var(--bm-text-muted)' }}>
                    {fmtAsOf(s.asOf)}{s.fresh.stale ? ' · STALE' : ''}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 11, color: 'var(--bm-text-subtle)', padding: '8px 12px', lineHeight: 1.5 }}>
          Real ingested data only: CDC NWSS wastewater · WHO DON official events · CoV-Spectrum LAPIS
          variant frequencies. fuseSignals() promotes corroborated signals to confirmed and, when
          sources report disagreeing counts, keeps the highest-confidence value as primary while
          flagging the divergence (⚠ N vs M) — the alternates are retained in provenance, never
          silently overwritten. See designs/biomax-epi/pacc-data-sourcing.md.
        </div>
      </div>
    </div>
  );
}
